// src/components/pages/ListViewer.js
import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import Table from '../saved-lists-components/Table';
import { db, auth } from '../../firebase-config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { ulid } from 'ulid';

export default function ListViewer({ accountType }) {
  const { id, type }     = useParams();                // /list-viewer/:type/:id
  const location         = useLocation();
  const navigate         = useNavigate();
  const [user]           = useAuthState(auth);

  // initial from link-state if present
  const initialSearch    = location.state?.search ?? null;
  const initialResults   = initialSearch?.results ?? null;

  const [search,  setSearch]  = useState(initialSearch);
  const [results, setResults] = useState(initialResults);

  /** Ensure each row has id, checked, notes, tags */
  const normaliseRows = arr => {
    let changed = false;
    const out = arr.map((r, index) => {
      const row = { ...r };
      if (!row.id) { row.id = (index + 1).toString(); changed = true; }
      if (row.checked === undefined) { row.checked = false; changed = true; }
      if (row.notes   === undefined) { row.notes   = '';    changed = true; }
      if (!Array.isArray(row.tags))  { row.tags    = [];    changed = true; }
      return row;
    });
    return changed ? out : arr;
  };

  /* ─── On mount: if we came via link-state, normalise & persist ─── */
  useEffect(() => {
    if (!results || !search) return;

    const fixed = normaliseRows(results);
    if (fixed !== results) {
      setResults(fixed);
      // write back to Firestore
      updateDoc(
        doc(db, 'searches', user.uid, `${type}Searches`, id),
        { results: fixed }
      ).catch(console.error);
    }
  }, [results, search, user, type, id]);

  /* ─── Fetch from Firestore on refresh ─── */
  useEffect(() => {
    if (search || !user || !type || !id) return;

    (async () => {
      try {
        const ref  = doc(db, 'searches', user.uid, `${type}Searches`, id);
        const snap = await getDoc(ref);
        if (!snap.exists()) return navigate('/my-lists');

        const data  = snap.data();
        const fixed = normaliseRows(data.results);

        setSearch({ id, ...data, type: type[0].toUpperCase()+type.slice(1) });
        setResults(fixed);

        if (fixed !== data.results) {
          await updateDoc(ref, { results: fixed });
        }
      } catch (err) {
        console.error(err);
        navigate('/my-lists');
      }
    })();
  }, [search, user, type, id, navigate]);

  /* ─── Helpers: patch a single change & persist ─── */
  const patchDoc = async updated => {
    setResults(updated);
    try {
      await updateDoc(
        doc(db, 'searches', user.uid, `${type}Searches`, id),
        { results: updated }
      );
    } catch (err) {
      console.error('Firestore update failed:', err);
      // rollback
      setResults(results);
    }
  };

  const updateCheckedState = (rowId, checked) =>
    patchDoc(results.map(r => r.id === rowId ? { ...r, checked } : r));

  const updateNotes = (rowId, notes) =>
    patchDoc(results.map(r => r.id === rowId ? { ...r, notes } : r));

  const updateTags = (rowId, tags) =>
    patchDoc(results.map(r => r.id === rowId ? { ...r, tags } : r));

  if (!results) return <p className="loading">Loading…</p>;

return (
  <div className="table-container">
    <Table
      key={id}
      data={results}
      updateCheckedState={updateCheckedState}
      updateNotes={updateNotes}
      updateTags={updateTags}
      listType={type.toLowerCase()}
      accountType={accountType}
    />
  </div>
);
}
